import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Analytics } from '@vercel/analytics/react';
import Header from './components/Header';
import TabBasePromo from './components/TabBasePromo';
import { INSTRUMENTS, SLUG_TO_INSTRUMENT, INSTRUMENT_TO_SLUG, VALID_INSTRUMENTS } from './lib/instruments';
import { parseDeepLink, carryView } from './lib/deep-link';
import { ALL_KEYS, arpeggioTypesFor, ARPEGGIO_TYPE_SWAP } from './lib/theory';
import ControlPanel, { GuitarQuickButtons } from './components/ControlPanel';
import { InstrumentLayout, KeyTitle, NotesInKey } from './components/shared';
import { useIsDesktop } from './hooks/useIsDesktop';
import { getGuitarTitle, getNotesInKey, getNotesInChord, notesHeading } from './lib/notes-row';
import Fretboard from './components/Fretboard';
import { useLessonAccess } from './hooks/useLessonAccess';
import {
  lessonTraysFor, findLessonPattern, getBluesTitle, getBluesNotes, BLUES_NOTES_HEADING,
} from './lib/lesson-patterns';
import MandolinControls, { MandolinQuickButtons } from './components/MandolinControls';
import MandolinFretboard from './components/MandolinFretboard';
import BanjoControls, { BanjoQuickButtons } from './components/BanjoControls';
import BanjoFretboard from './components/BanjoFretboard';
import { getChordTitle, getChordNotes } from './lib/banjo-theory';
import DobroControls, { DobroQuickButtons, dobroStructureOf, dobroStateFromStructure } from './components/DobroControls';
import DobroFretboard from './components/DobroFretboard';
import UprightBassControls, { UprightBassQuickButtons } from './components/UprightBassControls';
import UprightBassFingerboard from './components/UprightBassFingerboard';
import { getBassTitle, getBassNotesRow } from './lib/upright-bass-theory';
import FiddleControls, { FiddleQuickButtons } from './components/FiddleControls';
import FiddleFingerboard from './components/FiddleFingerboard';
import { getFiddleTitle, getFiddleNotesRow } from './lib/fiddle-theory';

const INITIAL_GUITAR_STRING_FOCUS = [true, true, true, true, true, true];
const INITIAL_MANDOLIN_STRING_FOCUS = [true, true, true, true];
const INITIAL_BANJO_STRING_FOCUS    = [true, true, true, true, true];
const INITIAL_DOBRO_STRING_FOCUS       = [true, true, true, true, true, true];
const INITIAL_UPRIGHT_BASS_STRING_FOCUS = [true, true, true, true]; // E, A, D, G
const INITIAL_FIDDLE_STRING_FOCUS       = [true, true, true, true]; // G, D, A, E

// ── URL ↔ instrument slug helpers ───────────────────────────────────────────
function instrumentFromPath() {
  const slug = window.location.pathname.replace(/^\//, '').toLowerCase();
  return SLUG_TO_INSTRUMENT[slug] ?? 'guitar';
}

// Snapshot the query string at module load — the URL-sync effect calls
// pushState() which replaces the URL and wipes window.location.search,
// so deep-link params must be captured before any effect runs.
const INITIAL_SEARCH = typeof window !== 'undefined' ? window.location.search : '';

function initialInstrumentFromUrl() {
  if (typeof window === 'undefined') return 'guitar';
  const params = new URLSearchParams(INITIAL_SEARCH);
  const inst = params.get('instrument');
  if (inst && VALID_INSTRUMENTS.includes(inst)) return inst;
  return instrumentFromPath();
}

const BANJO_STRUCTURE_DEFAULTS = {
  'Full Scale':  { shape: 'F Shape' },
  'Pentatonic':  { shape: 'F Shape' },
  'Chords':      { shape: 'F Shape' },
  'Modes':       { shape: 'F Shape' },
};

/** Mandolin structures shown in the redesign (Chop Chords and Double Stops are hidden, code kept). */
const MANDOLIN_STRUCTURES = ['Full Scale', 'Pentatonic', 'Modes', 'Arpeggios'];

function App() {
  const [instrument, setInstrument] = useState(initialInstrumentFromUrl);
  const isDesktop = useIsDesktop();

  // ── Shared across instruments ────────────────────────────────────────────────
  // Label persists when switching instruments so user doesn't have to re-enable
  // 'Intervals' or 'Note Names' on each fretboard.
  // 'None' is hidden in the redesign (rebuild plan, open question 1), so Note Names is the default.
  const [label, setLabel] = useState('Note Names');

  // ── Guitar State ─────────────────────────────────────────────────────────────
  const [guitarKey,         setGuitarKey]         = useState('G');
  const [guitarTonality,    setGuitarTonality]    = useState('Major');   // 'Major' | 'Minor'
  const [guitarStructure,   setGuitarStructure]   = useState('Full Scale'); // 'Full Scale' | 'Pentatonic' | 'Modes' | 'Arpeggios'
  const [guitarMode,        setGuitarMode]        = useState('Ionian');
  // CAGED is back as a Shape tray (Oct 2026); the 3 NPS system stays hidden, so the system is
  // pinned to CAGED. 'All Shapes' draws the whole neck.
  const guitarSystem = 'CAGED';
  const [guitarShape,       setGuitarShape]       = useState('All Shapes'); // 'All Shapes' | 'C Shape' | 'A Shape' | 'G Shape' | 'E Shape' | 'D Shape'
  const [guitarStringFocus, setGuitarStringFocus] = useState(INITIAL_GUITAR_STRING_FOCUS);
  const [guitarFilter,      setGuitarFilter]      = useState('All Notes');

  // ── Guitar Arpeggios State ───────────────────────────────────────────────────
  const [guitarArpeggioType, setGuitarArpeggioType] = useState('Major');

  // ── Guitar lesson patterns (private; see "Private lesson patterns" in CLAUDE.md) ─
  // The one switch is the Sign in: `lessonData` is the server's pattern data, or null when signed
  // out. Null means no Lesson trays and this id is never set, so the page is the public page.
  // The stored id only counts while the current view offers it (the Scale view in Major).
  const { patterns: lessonData } = useLessonAccess();
  const [guitarLessonPatternId, setGuitarLessonPatternId] = useState(null);
  const guitarLessonTrays = useMemo(
    () => lessonTraysFor(lessonData, guitarStructure, guitarTonality),
    [lessonData, guitarStructure, guitarTonality]
  );
  const guitarLessonPattern = useMemo(
    () => findLessonPattern(guitarLessonTrays, guitarLessonPatternId),
    [guitarLessonTrays, guitarLessonPatternId]
  );
  const isGuitarBluesLesson = guitarLessonPattern?.group === 'blues';

  // ── Banjo State ──────────────────────────────────────────────────────────────
  const [banjoKey,         setBanjoKey]         = useState('G');
  const [banjoTonality,    setBanjoTonality]    = useState('Major');   // 'Major' | 'Minor'
  const [banjoStructure,   setBanjoStructure]   = useState('Full Scale');
  const [banjoMode,        setBanjoMode]        = useState('Ionian');
  const [banjoShape,       setBanjoShape]       = useState('F Shape');
  const [banjoStringFocus, setBanjoStringFocus] = useState(INITIAL_BANJO_STRING_FOCUS);
  const [banjoFilter,      setBanjoFilter]      = useState('All Notes');

  // ── Dobro State ──────────────────────────────────────────────────────────────
  const [dobroCategory,    setDobroCategory]    = useState('SCALES');
  const [dobroType,        setDobroType]        = useState('Full Scale');
  const [dobroMode,        setDobroMode]        = useState('Ionian');
  const [dobroTonality,    setDobroTonality]    = useState('Major');   // 'Major' | 'Minor'
  const [dobroKey,         setDobroKey]         = useState('G');
  const [dobroShape,       setDobroShape]       = useState('All Shapes');
  const [dobroStringFocus, setDobroStringFocus] = useState(INITIAL_DOBRO_STRING_FOCUS);
  const [dobroFilter,      setDobroFilter]      = useState('All Notes');

  // ── Mandolin State ───────────────────────────────────────────────────────────
  const [mandolinKey,         setMandolinKey]         = useState('G');
  const [mandolinTonality,    setMandolinTonality]    = useState('Major');   // 'Major' | 'Minor'
  const [mandolinStructure,   setMandolinStructure]   = useState('Full Scale');
  const [mandolinMode,        setMandolinMode]        = useState('Ionian');
  // The System row (Standard / FFcP) and the Shape row are hidden in the redesign, so these never
  // change: the fretboard always draws the standard view. Kept as props for when they return.
  const mandolinSystem = 'Standard';
  const mandolinShape  = 'All Shapes';
  const [mandolinStringFocus, setMandolinStringFocus] = useState(INITIAL_MANDOLIN_STRING_FOCUS);
  const [mandolinFilter,      setMandolinFilter]      = useState('All Notes');
  const [mandolinArpeggioType, setMandolinArpeggioType] = useState('Major');

  // ── Upright Bass State ───────────────────────────────────────────────────────
  const [uprightBassKey,              setUprightBassKey]              = useState('G');
  const [uprightBassTonality,         setUprightBassTonality]         = useState('Major');
  const [uprightBassStructure,        setUprightBassStructure]        = useState('Full Scale');
  const [uprightBassStringFocus,      setUprightBassStringFocus]      = useState(INITIAL_UPRIGHT_BASS_STRING_FOCUS);
  const [uprightBassFilter,           setUprightBassFilter]           = useState('All Notes');
  const [uprightBassLabel,            setUprightBassLabel]            = useState('Note Names'); // 'None' is hidden in the redesign
  const [uprightBassChordChangesActive, setUprightBassChordChangesActive] = useState(false);
  const [uprightBassSelectedDegrees,  setUprightBassSelectedDegrees]  = useState([1, 4, 5]);

  // ── Fiddle State ─────────────────────────────────────────────────────────────
  // Tuned G D A E like the mandolin, so the theory is shared; what differs is
  // that there are no frets, hence `position` (a hand frame) instead of a shape,
  // and `guides` for the fretless-only overlays.
  const [fiddleKey,         setFiddleKey]         = useState('G');
  const [fiddleTonality,    setFiddleTonality]    = useState('Major');   // 'Major' | 'Minor'
  const [fiddleStructure,   setFiddleStructure]   = useState('Full Scale');
  const [fiddleMode,        setFiddleMode]        = useState('Ionian');
  const [fiddleShape,       setFiddleShape]       = useState('3rds');    // double-stop interval
  const [fiddlePosition,    setFiddlePosition]    = useState('1st');
  const [fiddleStringFocus, setFiddleStringFocus] = useState(INITIAL_FIDDLE_STRING_FOCUS);
  const [fiddleFilter,      setFiddleFilter]      = useState('All Notes');
  // Own label state rather than the shared one: 'Fingering' means nothing on a
  // fretted instrument, so leaking it across would blank the other pages' labels.
  const [fiddleLabel,       setFiddleLabel]       = useState('Note Names');
  const [fiddleGuides,      setFiddleGuides]      = useState(['Tapes', 'Half Steps']);
  const [fiddleArpeggioType, setFiddleArpeggioType] = useState('Major');

  // ── URL sync ─────────────────────────────────────────────────────────────────
  // Keep the address bar in sync so Vercel Analytics sees the correct path.
  // First run uses replaceState so deep-link query strings (?key=A&...) are
  // cleaned out without leaving a junk history entry the user could navigate to.
  const isFirstUrlSync = useRef(true);
  useEffect(() => {
    const slug = INSTRUMENT_TO_SLUG[instrument] ?? 'guitar';
    const target = '/' + slug;
    if (window.location.pathname !== target || window.location.search) {
      const method = isFirstUrlSync.current ? 'replaceState' : 'pushState';
      window.history[method]({ instrument }, '', target);
    }
    // The first load already has the right <title> and description, baked into
    // the static HTML for that path (generate-instrument-pages.mjs; "/" keeps
    // the homepage's). Only an in-app switch needs them swapped.
    if (!isFirstUrlSync.current) {
      const meta = INSTRUMENTS.find((i) => i.key === instrument);
      if (meta) {
        document.title = meta.title;
        document.querySelector('meta[name="description"]')?.setAttribute('content', meta.description);
      }
    }
    isFirstUrlSync.current = false;
  }, [instrument]);

  // Handle browser back / forward
  useEffect(() => {
    const onPopState = () => instrumentChangeRef.current(instrumentFromPath());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // ── Dobro Handlers ──────────────────────────────────────────────────────────
  // The redesign's single Structure tray (Scale / Penta / Modes / Chords) maps onto the stored
  // category + type + shape; see dobroStateFromStructure in DobroControls.
  const handleDobroStructureChange = useCallback((structure) => {
    const next = dobroStateFromStructure(structure);
    setDobroCategory(next.category);
    setDobroType(next.type);
    setDobroShape(next.shape);
  }, []);

  const handleDobroStringToggle = useCallback((idx) => {
    setDobroStringFocus(prev => prev.map((val, i) => i === idx ? !val : val));
  }, []);

  // ‹ › in the title: step through the keys in cycle-of-fifths order, wrapping at the ends
  const handleDobroKeyStep = useCallback((delta) => {
    setDobroKey(k => ALL_KEYS[(ALL_KEYS.indexOf(k) + delta + ALL_KEYS.length) % ALL_KEYS.length]);
  }, []);

  // Reset quick button: keeps the key and puts everything else the dobro page controls back
  // to its default, i.e. the major scale in the current key. (The Labels choice is shared across
  // instruments, so it resets too.)
  const handleDobroReset = useCallback(() => {
    setDobroTonality('Major');
    setDobroCategory('SCALES');
    setDobroType('Full Scale');
    setDobroShape('All Shapes');
    setDobroMode('Ionian');
    setDobroStringFocus(INITIAL_DOBRO_STRING_FOCUS);
    setDobroFilter('All Notes');
    setLabel('Note Names');
  }, []);

  // ── Banjo Handlers ───────────────────────────────────────────────────────────
  const handleBanjoStructureChange = useCallback((structure) => {
    // Ignore a structure the banjo does not have (a deep link may name one), and a click on the
    // structure that is already selected (a Tray reports those) so it does not reset the chord shape.
    if (!BANJO_STRUCTURE_DEFAULTS[structure] || structure === banjoStructure) return;
    setBanjoStructure(structure);
    setBanjoShape(BANJO_STRUCTURE_DEFAULTS[structure].shape);
  }, [banjoStructure]);

  const handleBanjoStringToggle = useCallback((index) => {
    setBanjoStringFocus(prev => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  }, []);

  // ‹ › in the title: step through the keys in cycle-of-fifths order, wrapping at the ends
  const handleBanjoKeyStep = useCallback((delta) => {
    setBanjoKey(k => ALL_KEYS[(ALL_KEYS.indexOf(k) + delta + ALL_KEYS.length) % ALL_KEYS.length]);
  }, []);

  // Reset quick button: keeps the key and puts everything else the banjo page controls back
  // to its default, i.e. the major scale in the current key. (The Labels choice is shared across
  // instruments, so it resets too.)
  const handleBanjoReset = useCallback(() => {
    setBanjoTonality('Major');
    setBanjoStructure('Full Scale');
    setBanjoMode('Ionian');
    setBanjoShape('F Shape');
    setBanjoStringFocus(INITIAL_BANJO_STRING_FOCUS);
    setBanjoFilter('All Notes');
    setLabel('Note Names');
  }, []);

  // ── Guitar Handlers ──────────────────────────────────────────────────────────

  const handleGuitarStructureChange = useCallback((structure) => {
    // Ignore a click on the structure that is already selected (a Tray reports those), so
    // re-clicking Arps does not un-mute every string again.
    if (structure === guitarStructure) return;
    setGuitarStructure(structure);
    if (structure === 'Arpeggios') {
      // Reset to all strings so arpeggio tones are visible across the full neck on entry
      setGuitarStringFocus(INITIAL_GUITAR_STRING_FOCUS);
      // Ensure the type is valid for the current tonality (e.g. 'Major' is invalid for Minor)
      setGuitarArpeggioType(t => {
        const valid = arpeggioTypesFor(guitarTonality).all;
        return valid.includes(t) ? t : (guitarTonality === 'Major' ? 'Major' : 'Minor');
      });
    }
  }, [guitarStructure, guitarTonality]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGuitarTonalityChange = useCallback((newTonality) => {
    // A Tray also reports a click on the option that is already selected; without this guard
    // the arpeggio type below would swap (Minor -> Major) with no tonality change behind it.
    if (newTonality === guitarTonality) return;
    setGuitarTonality(newTonality);
    // When Arpeggios is active, swap the type if it has a tonality variant
    if (guitarStructure === 'Arpeggios') {
      setGuitarArpeggioType(t => ARPEGGIO_TYPE_SWAP[t] ?? t);
    }
  }, [guitarStructure, guitarTonality]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGuitarStringToggle = useCallback((index) => {
    setGuitarStringFocus(prev => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  }, []);

  // A lesson pattern and a CAGED shape both pick a position on the neck, so choosing one clears
  // the other. Tapping the lesson pattern that is on turns it off again.
  const handleGuitarShapeChange = useCallback((shape) => {
    setGuitarShape(shape);
    setGuitarLessonPatternId(null);
  }, []);

  const handleGuitarLessonPatternChange = useCallback((id) => {
    setGuitarLessonPatternId(prev => (prev === id ? null : id));
    setGuitarShape('All Shapes');
  }, []);

  // ‹ › in the title: step through the keys in cycle-of-fifths order, wrapping at the ends
  const handleGuitarKeyStep = useCallback((delta) => {
    setGuitarKey(k => ALL_KEYS[(ALL_KEYS.indexOf(k) + delta + ALL_KEYS.length) % ALL_KEYS.length]);
  }, []);

  // Reset quick button: keeps the key and puts everything else the guitar page controls back
  // to its default, i.e. the major scale in the current key. (The Labels choice is shared across
  // instruments, so it resets too.)
  const handleGuitarReset = useCallback(() => {
    setGuitarTonality('Major');
    setGuitarStructure('Full Scale');
    setGuitarMode('Ionian');
    setGuitarArpeggioType('Major');
    setGuitarShape('All Shapes');
    setGuitarLessonPatternId(null);
    setGuitarStringFocus(INITIAL_GUITAR_STRING_FOCUS);
    setGuitarFilter('All Notes');
    setLabel('Note Names');
  }, []);


  // ── Mandolin Handlers ────────────────────────────────────────────────────────
  const handleMandolinStructureChange = useCallback((structure) => {
    // Ignore a structure the mandolin page does not offer (a deep link may name one), and a click
    // on the one already selected (a Tray reports those) so re-clicking Arps does not un-mute strings.
    if (!MANDOLIN_STRUCTURES.includes(structure) || structure === mandolinStructure) return;
    setMandolinStructure(structure);
    if (structure === 'Arpeggios') {
      // Same as guitar: every string on, and a type that exists for the current tonality.
      setMandolinStringFocus(INITIAL_MANDOLIN_STRING_FOCUS);
      setMandolinArpeggioType(t => {
        const valid = arpeggioTypesFor(mandolinTonality).all;
        return valid.includes(t) ? t : (mandolinTonality === 'Major' ? 'Major' : 'Minor');
      });
    }
  }, [mandolinStructure, mandolinTonality]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMandolinTonalityChange = useCallback((newTonality) => {
    if (newTonality === mandolinTonality) return;
    setMandolinTonality(newTonality);
    // Keep the chosen arpeggio through the flip (Maj7 <-> Min7)
    if (mandolinStructure === 'Arpeggios') {
      setMandolinArpeggioType(t => ARPEGGIO_TYPE_SWAP[t] ?? t);
    }
  }, [mandolinStructure, mandolinTonality]); // eslint-disable-line react-hooks/exhaustive-deps

  // ‹ › in the title: step through the keys in cycle-of-fifths order, wrapping at the ends
  const handleMandolinKeyStep = useCallback((delta) => {
    setMandolinKey(k => ALL_KEYS[(ALL_KEYS.indexOf(k) + delta + ALL_KEYS.length) % ALL_KEYS.length]);
  }, []);

  // Reset quick button: keeps the key and puts everything else the mandolin page controls back
  // to its default, i.e. the major scale in the current key. (The Labels choice is shared across
  // instruments, so it resets too.)
  const handleMandolinReset = useCallback(() => {
    setMandolinTonality('Major');
    setMandolinStructure('Full Scale');
    setMandolinMode('Ionian');
    setMandolinArpeggioType('Major');
    setMandolinStringFocus(INITIAL_MANDOLIN_STRING_FOCUS);
    setMandolinFilter('All Notes');
    setLabel('Note Names');
  }, []);

  const handleMandolinStringToggle = useCallback((index) => {
    setMandolinStringFocus(prev => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  }, []);

  // ── Upright Bass Handlers ────────────────────────────────────────────────────
  const handleUprightBassStructureChange = useCallback((newStructure) => {
    setUprightBassStructure(newStructure);
    // Deactivate Chord Changes when leaving Root & 5th; preserve degree selection
    if (newStructure !== 'Root & 5th') {
      setUprightBassChordChangesActive(false);
    }
  }, []);

  const handleUprightBassStringToggle = useCallback((index) => {
    setUprightBassStringFocus(prev => prev.map((val, i) => (i === index ? !val : val)));
  }, []);

  const handleUprightBassDegreeToggle = useCallback((degree) => {
    setUprightBassSelectedDegrees(prev =>
      prev.includes(degree)
        ? prev.filter(d => d !== degree)
        : [...prev, degree].sort((a, b) => a - b)
    );
  }, []);

  // ‹ › in the title: step through the keys in cycle-of-fifths order, wrapping at the ends
  const handleUprightBassKeyStep = useCallback((delta) => {
    setUprightBassKey(k => ALL_KEYS[(ALL_KEYS.indexOf(k) + delta + ALL_KEYS.length) % ALL_KEYS.length]);
  }, []);

  // Reset quick button: keeps the key and puts everything else the upright bass page controls back
  // to its default, i.e. the major scale in the current key.
  const handleUprightBassReset = useCallback(() => {
    setUprightBassTonality('Major');
    setUprightBassStructure('Full Scale');
    setUprightBassStringFocus(INITIAL_UPRIGHT_BASS_STRING_FOCUS);
    setUprightBassFilter('All Notes');
    setUprightBassLabel('Note Names');
    setUprightBassChordChangesActive(false);
    setUprightBassSelectedDegrees([1, 4, 5]);
  }, []);

  // ── Fiddle Handlers ──────────────────────────────────────────────────────────
  const handleFiddleStructureChange = useCallback((structure) => {
    // Ignore a click on the structure that is already selected (a Tray reports those), so
    // re-clicking Arps does not un-mute every string again.
    if (structure === fiddleStructure) return;
    setFiddleStructure(structure);
    if (structure === 'Arpeggios') {
      // Show the whole chord across the instrument on entry, and make sure the
      // carried-over type actually exists for the current tonality — 'Major' has
      // no :Minor entry in ARPEGGIO_DICT and would render an empty board.
      setFiddleStringFocus(INITIAL_FIDDLE_STRING_FOCUS);
      setFiddleArpeggioType(t => {
        const valid = arpeggioTypesFor(fiddleTonality).all;
        return valid.includes(t) ? t : (fiddleTonality === 'Minor' ? 'Minor' : 'Major');
      });
    }
    // Reset to the most common double stop rather than keeping whatever
    // interval was last chosen on a previous visit to this structure.
    if (structure === 'Double Stops') setFiddleShape('3rds');
  }, [fiddleStructure, fiddleTonality]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFiddleTonalityChange = useCallback((newTonality) => {
    // A Tray also reports a click on the option that is already selected; without this guard
    // the arpeggio type below would swap (Minor -> Major) with no tonality change behind it.
    if (newTonality === fiddleTonality) return;
    setFiddleTonality(newTonality);
    // Keep the chosen arpeggio through the flip: Maj7 becomes Min7 rather than
    // collapsing back to the plain triad. Tonality-agnostic types stay put.
    if (fiddleStructure === 'Arpeggios') {
      setFiddleArpeggioType(t => ARPEGGIO_TYPE_SWAP[t] ?? t);
    }
  }, [fiddleStructure, fiddleTonality]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFiddleStringToggle = useCallback((index) => {
    setFiddleStringFocus(prev => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  }, []);

  const handleFiddleGuideToggle = useCallback((guide) => {
    setFiddleGuides(prev =>
      prev.includes(guide) ? prev.filter(g => g !== guide) : [...prev, guide]
    );
  }, []);

  // ‹ › in the title: step through the keys in cycle-of-fifths order, wrapping at the ends
  const handleFiddleKeyStep = useCallback((delta) => {
    setFiddleKey(k => ALL_KEYS[(ALL_KEYS.indexOf(k) + delta + ALL_KEYS.length) % ALL_KEYS.length]);
  }, []);

  // Reset quick button: keeps the key and puts everything else the fiddle page controls back
  // to its default, i.e. the major scale in the current key.
  const handleFiddleReset = useCallback(() => {
    setFiddleTonality('Major');
    setFiddleStructure('Full Scale');
    setFiddleMode('Ionian');
    setFiddleShape('3rds');
    setFiddlePosition('1st');
    setFiddleArpeggioType('Major');
    setFiddleStringFocus(INITIAL_FIDDLE_STRING_FOCUS);
    setFiddleFilter('All Notes');
    setFiddleLabel('Note Names');
    setFiddleGuides(['Tapes', 'Half Steps']);
  }, []);

  // ── Apply a resolved view (deep link or instrument switch) ───────────────────
  // Goes through the same handlers the controls use, so each instrument's structure rules
  // (arpeggio string reset, banjo shape, dobro category) still apply.
  const applyView = (link) => {
    const { key, tonality, structure, mode, arpeggioType } = link;

    setInstrument(link.instrument);

    if (link.instrument === 'guitar') {
      setGuitarKey(key);
      handleGuitarStructureChange(structure);
      setGuitarTonality(tonality);
      setGuitarMode(mode);
      setGuitarArpeggioType(arpeggioType);
    }
    if (link.instrument === 'mandolin') {
      setMandolinKey(key);
      handleMandolinStructureChange(structure);
      setMandolinTonality(tonality);
      setMandolinMode(mode);
      setMandolinArpeggioType(arpeggioType);
    }
    if (link.instrument === 'banjo') {
      setBanjoKey(key);
      handleBanjoStructureChange(structure);
      setBanjoTonality(tonality);
      setBanjoMode(mode);
    }
    if (link.instrument === 'dobro') {
      setDobroKey(key);
      setDobroTonality(tonality);
      handleDobroStructureChange(structure);
      setDobroMode(mode);
    }
    if (link.instrument === 'fiddle') {
      setFiddleKey(key);
      setFiddleTonality(tonality);
      handleFiddleStructureChange(structure);
      setFiddleMode(mode);
      setFiddleArpeggioType(arpeggioType);
    }
    if (link.instrument === 'uprightBass') {
      setUprightBassKey(key);
      setUprightBassTonality(tonality);
      handleUprightBassStructureChange(structure);
    }
  };

  // ── Deep-link from SEO pages ─────────────────────────────────────────────────
  // Reads ?instrument=mandolin&key=G&structure=Pentatonic&tonality=Minor&mode=Dorian
  // from the URL and pre-loads the correct instrument + scale state.
  // Reads from INITIAL_SEARCH (snapshot at module load) — by the time this
  // effect runs the URL-sync effect has already wiped window.location.search.
  useEffect(() => {
    // parseDeepLink (src/lib/deep-link.js) holds the validation and fallback rules.
    const link = parseDeepLink(INITIAL_SEARCH);
    if (link) applyView(link);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Instrument switch ────────────────────────────────────────────────────────
  // The key, tonality and structure follow the user to the next instrument: D major on the
  // mandolin opens D major on the guitar. carryView (src/lib/deep-link.js) maps a structure the
  // next instrument lacks the same way a deep link would.
  const currentView = () => {
    switch (instrument) {
      case 'mandolin':
        return { key: mandolinKey, tonality: mandolinTonality, structure: mandolinStructure, mode: mandolinMode, arpeggioType: mandolinArpeggioType };
      case 'banjo':
        return { key: banjoKey, tonality: banjoTonality, structure: banjoStructure, mode: banjoMode };
      case 'dobro':
        return { key: dobroKey, tonality: dobroTonality, structure: dobroStructureOf(dobroCategory, dobroType), mode: dobroMode };
      case 'fiddle':
        return { key: fiddleKey, tonality: fiddleTonality, structure: fiddleStructure, mode: fiddleMode, arpeggioType: fiddleArpeggioType };
      case 'uprightBass':
        return { key: uprightBassKey, tonality: uprightBassTonality, structure: uprightBassStructure };
      default:
        return { key: guitarKey, tonality: guitarTonality, structure: guitarStructure, mode: guitarMode, arpeggioType: guitarArpeggioType };
    }
  };

  const handleInstrumentChange = (next) => {
    if (next === instrument) return;
    const link = carryView(currentView(), next);
    if (link) applyView(link);
    else setInstrument(next);
  };

  // Back / forward carries the view too; the popstate listener is registered once, so it reads
  // the latest handler through a ref.
  const instrumentChangeRef = useRef(handleInstrumentChange);
  instrumentChangeRef.current = handleInstrumentChange;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <Header instrument={instrument} onInstrumentChange={handleInstrumentChange} />
      <TabBasePromo />

      {instrument === 'guitar' && (
        <InstrumentLayout
          title={
            <KeyTitle
              size={isDesktop ? 'md' : 'sm'}
              title={isGuitarBluesLesson ? getBluesTitle(guitarKey) : getGuitarTitle({
                key: guitarKey, tonality: guitarTonality, structure: guitarStructure,
                mode: guitarMode, arpeggioType: guitarArpeggioType,
              })}
              onPrev={() => handleGuitarKeyStep(-1)}
              onNext={() => handleGuitarKeyStep(1)}
            />
          }
          board={
            <Fretboard
              guitarKey={guitarKey}
              guitarTonality={guitarTonality}
              guitarStructure={guitarStructure}
              guitarMode={guitarMode}
              guitarSystem={guitarSystem}
              guitarShape={guitarShape}
              guitarStringFocus={guitarStringFocus}
              guitarFilter={guitarFilter}
              guitarLabel={label}
              guitarArpeggioType={guitarArpeggioType}
              guitarLessonPattern={guitarLessonPattern}
            />
          }
          notes={
            <NotesInKey
              size={isDesktop ? 'md' : 'sm'}
              heading={isGuitarBluesLesson ? BLUES_NOTES_HEADING : notesHeading(guitarStructure)}
              notes={
                isGuitarBluesLesson
                  ? getBluesNotes(guitarKey)
                  : guitarStructure === 'Arpeggios'
                  ? getNotesInChord({ key: guitarKey, tonality: guitarTonality, arpeggioType: guitarArpeggioType })
                  : getNotesInKey({ key: guitarKey, tonality: guitarTonality, structure: guitarStructure, mode: guitarMode })
              }
            />
          }
          controls={
            <ControlPanel
              guitarKey={guitarKey}
              onGuitarKeyChange={setGuitarKey}
              guitarTonality={guitarTonality}
              onGuitarTonalityChange={handleGuitarTonalityChange}
              guitarStructure={guitarStructure}
              onGuitarStructureChange={handleGuitarStructureChange}
              guitarMode={guitarMode}
              onGuitarModeChange={setGuitarMode}
              guitarSystem={guitarSystem}
              guitarShape={guitarShape}
              guitarStringFocus={guitarStringFocus}
              onGuitarStringToggle={handleGuitarStringToggle}
              guitarArpeggioType={guitarArpeggioType}
              onGuitarArpeggioTypeChange={setGuitarArpeggioType}
              onGuitarShapeChange={handleGuitarShapeChange}
              lessonTrays={guitarLessonTrays}
              guitarLessonPattern={guitarLessonPattern ? guitarLessonPattern.id : null}
              onGuitarLessonPatternChange={handleGuitarLessonPatternChange}
            />
          }
          quickButtons={
            <GuitarQuickButtons
              guitarStructure={guitarStructure}
              guitarFilter={guitarFilter}
              onGuitarFilterChange={setGuitarFilter}
              guitarLabel={label}
              onGuitarLabelChange={setLabel}
              onGuitarReset={handleGuitarReset}
            />
          }
        />
      )}

      {instrument === 'mandolin' && (
        <InstrumentLayout
          title={
            <KeyTitle
              size={isDesktop ? 'md' : 'sm'}
              title={getGuitarTitle({
                key: mandolinKey, tonality: mandolinTonality, structure: mandolinStructure,
                mode: mandolinMode, arpeggioType: mandolinArpeggioType,
              })}
              onPrev={() => handleMandolinKeyStep(-1)}
              onNext={() => handleMandolinKeyStep(1)}
            />
          }
          board={
            <MandolinFretboard
              mandolinKey={mandolinKey}
              mandolinTonality={mandolinTonality}
              mandolinStructure={mandolinStructure}
              mandolinMode={mandolinMode}
              mandolinSystem={mandolinSystem}
              mandolinShape={mandolinShape}
              mandolinStringFocus={mandolinStringFocus}
              mandolinFilter={mandolinFilter}
              mandolinLabel={label}
              mandolinArpeggioType={mandolinArpeggioType}
            />
          }
          notes={
            <NotesInKey
              size={isDesktop ? 'md' : 'sm'}
              heading={notesHeading(mandolinStructure)}
              notes={
                mandolinStructure === 'Arpeggios'
                  ? getNotesInChord({ key: mandolinKey, tonality: mandolinTonality, arpeggioType: mandolinArpeggioType })
                  : getNotesInKey({ key: mandolinKey, tonality: mandolinTonality, structure: mandolinStructure, mode: mandolinMode })
              }
            />
          }
          controls={
            <MandolinControls
              mandolinKey={mandolinKey}
              onMandolinKeyChange={setMandolinKey}
              mandolinTonality={mandolinTonality}
              onMandolinTonalityChange={handleMandolinTonalityChange}
              mandolinStructure={mandolinStructure}
              onMandolinStructureChange={handleMandolinStructureChange}
              mandolinMode={mandolinMode}
              onMandolinModeChange={setMandolinMode}
              mandolinStringFocus={mandolinStringFocus}
              onMandolinStringToggle={handleMandolinStringToggle}
              mandolinArpeggioType={mandolinArpeggioType}
              onMandolinArpeggioTypeChange={setMandolinArpeggioType}
            />
          }
          quickButtons={
            <MandolinQuickButtons
              mandolinStructure={mandolinStructure}
              mandolinFilter={mandolinFilter}
              onMandolinFilterChange={setMandolinFilter}
              mandolinLabel={label}
              onMandolinLabelChange={setLabel}
              onMandolinReset={handleMandolinReset}
            />
          }
        />
      )}

      {instrument === 'banjo' && (
        <InstrumentLayout
          title={
            <KeyTitle
              size={isDesktop ? 'md' : 'sm'}
              title={
                banjoStructure === 'Chords'
                  ? getChordTitle(banjoKey, banjoTonality)
                  : getGuitarTitle({
                      key: banjoKey, tonality: banjoTonality, structure: banjoStructure, mode: banjoMode,
                    })
              }
              onPrev={() => handleBanjoKeyStep(-1)}
              onNext={() => handleBanjoKeyStep(1)}
            />
          }
          board={
            <BanjoFretboard
              banjoKey={banjoKey}
              banjoTonality={banjoTonality}
              banjoStructure={banjoStructure}
              banjoMode={banjoMode}
              banjoShape={banjoShape}
              banjoStringFocus={banjoStringFocus}
              banjoFilter={banjoFilter}
              banjoLabel={label}
            />
          }
          notes={
            <NotesInKey
              size={isDesktop ? 'md' : 'sm'}
              heading={notesHeading(banjoStructure)}
              notes={
                banjoStructure === 'Chords'
                  ? getChordNotes(banjoKey, banjoTonality)
                  : getNotesInKey({ key: banjoKey, tonality: banjoTonality, structure: banjoStructure, mode: banjoMode })
              }
            />
          }
          controls={
            <BanjoControls
              banjoKey={banjoKey}                 onBanjoKeyChange={setBanjoKey}
              banjoTonality={banjoTonality}       onBanjoTonalityChange={setBanjoTonality}
              banjoStructure={banjoStructure}     onBanjoStructureChange={handleBanjoStructureChange}
              banjoMode={banjoMode}               onBanjoModeChange={setBanjoMode}
              banjoShape={banjoShape}             onBanjoShapeChange={setBanjoShape}
              banjoStringFocus={banjoStringFocus} onBanjoStringToggle={handleBanjoStringToggle}
            />
          }
          quickButtons={
            <BanjoQuickButtons
              banjoStructure={banjoStructure}
              banjoFilter={banjoFilter}
              onBanjoFilterChange={setBanjoFilter}
              banjoLabel={label}
              onBanjoLabelChange={setLabel}
              onBanjoReset={handleBanjoReset}
            />
          }
        />
      )}

      {instrument === 'dobro' && (
        <InstrumentLayout
          title={
            <KeyTitle
              size={isDesktop ? 'md' : 'sm'}
              title={
                dobroCategory === 'CHORDS'
                  ? getChordTitle(dobroKey, dobroTonality)
                  : getGuitarTitle({
                      key: dobroKey, tonality: dobroTonality, structure: dobroType, mode: dobroMode,
                    })
              }
              onPrev={() => handleDobroKeyStep(-1)}
              onNext={() => handleDobroKeyStep(1)}
            />
          }
          board={
            <DobroFretboard
              dobroKey={dobroKey}
              dobroTonality={dobroTonality}
              dobroCategory={dobroCategory}
              dobroType={dobroType}
              dobroMode={dobroMode}
              dobroShape={dobroShape}
              dobroStringFocus={dobroStringFocus}
              dobroFilter={dobroFilter}
              dobroLabel={label}
            />
          }
          notes={
            <NotesInKey
              size={isDesktop ? 'md' : 'sm'}
              heading={notesHeading(dobroStructureOf(dobroCategory, dobroType))}
              notes={
                dobroCategory === 'CHORDS'
                  ? getChordNotes(dobroKey, dobroTonality)
                  : getNotesInKey({ key: dobroKey, tonality: dobroTonality, structure: dobroType, mode: dobroMode })
              }
            />
          }
          controls={
            <DobroControls
              dobroKey={dobroKey}                 onDobroKeyChange={setDobroKey}
              dobroTonality={dobroTonality}       onDobroTonalityChange={setDobroTonality}
              dobroStructure={dobroStructureOf(dobroCategory, dobroType)}
              onDobroStructureChange={handleDobroStructureChange}
              dobroMode={dobroMode}               onDobroModeChange={setDobroMode}
              dobroStringFocus={dobroStringFocus} onDobroStringToggle={handleDobroStringToggle}
            />
          }
          quickButtons={
            <DobroQuickButtons
              dobroStructure={dobroStructureOf(dobroCategory, dobroType)}
              dobroFilter={dobroFilter}
              onDobroFilterChange={setDobroFilter}
              dobroLabel={label}
              onDobroLabelChange={setLabel}
              onDobroReset={handleDobroReset}
            />
          }
        />
      )}

      {instrument === 'uprightBass' && (
        <InstrumentLayout
          notesBeside
          title={
            <KeyTitle
              size={isDesktop ? 'md' : 'sm'}
              title={getBassTitle({
                key: uprightBassKey, tonality: uprightBassTonality, structure: uprightBassStructure,
              })}
              onPrev={() => handleUprightBassKeyStep(-1)}
              onNext={() => handleUprightBassKeyStep(1)}
            />
          }
          board={
            <UprightBassFingerboard
              musicalKey={uprightBassKey}
              tonality={uprightBassTonality}
              structure={uprightBassStructure}
              stringFocus={uprightBassStringFocus}
              filter={uprightBassFilter}
              label={uprightBassLabel}
              chordChangesActive={uprightBassChordChangesActive}
              selectedDegrees={uprightBassSelectedDegrees}
            />
          }
          notes={(() => {
            const row = getBassNotesRow({
              key: uprightBassKey, tonality: uprightBassTonality, structure: uprightBassStructure,
            });
            return (
              <NotesInKey
                size={isDesktop ? 'md' : 'sm'}
                orientation="vertical"
                heading={row.heading}
                notes={row.notes}
              />
            );
          })()}
          controls={
            <UprightBassControls
              uprightBassKey={uprightBassKey}                       onUprightBassKeyChange={setUprightBassKey}
              uprightBassTonality={uprightBassTonality}             onUprightBassTonalityChange={setUprightBassTonality}
              uprightBassStructure={uprightBassStructure}           onUprightBassStructureChange={handleUprightBassStructureChange}
              uprightBassStringFocus={uprightBassStringFocus}       onUprightBassStringToggle={handleUprightBassStringToggle}
              uprightBassChordChangesActive={uprightBassChordChangesActive}
              onUprightBassChordChangesChange={setUprightBassChordChangesActive}
              uprightBassSelectedDegrees={uprightBassSelectedDegrees}
              onUprightBassDegreeToggle={handleUprightBassDegreeToggle}
            />
          }
          quickButtons={
            <UprightBassQuickButtons
              uprightBassChordChangesActive={uprightBassChordChangesActive}
              uprightBassFilter={uprightBassFilter}   onUprightBassFilterChange={setUprightBassFilter}
              uprightBassLabel={uprightBassLabel}     onUprightBassLabelChange={setUprightBassLabel}
              onUprightBassReset={handleUprightBassReset}
            />
          }
        />
      )}

      {instrument === 'fiddle' && (
        <InstrumentLayout
          notesBeside
          title={
            <KeyTitle
              size={isDesktop ? 'md' : 'sm'}
              title={getFiddleTitle({
                key: fiddleKey, tonality: fiddleTonality, structure: fiddleStructure,
                mode: fiddleMode, arpeggioType: fiddleArpeggioType,
              })}
              onPrev={() => handleFiddleKeyStep(-1)}
              onNext={() => handleFiddleKeyStep(1)}
            />
          }
          board={
            <FiddleFingerboard
              fiddleKey={fiddleKey}
              fiddleTonality={fiddleTonality}
              fiddleStructure={fiddleStructure}
              fiddleMode={fiddleMode}
              fiddleShape={fiddleShape}
              fiddlePosition={fiddlePosition}
              fiddleStringFocus={fiddleStringFocus}
              fiddleFilter={fiddleFilter}
              fiddleLabel={fiddleLabel}
              fiddleGuides={fiddleGuides}
              fiddleArpeggioType={fiddleArpeggioType}
            />
          }
          notes={(() => {
            const row = getFiddleNotesRow({
              key: fiddleKey, tonality: fiddleTonality, structure: fiddleStructure,
              mode: fiddleMode, arpeggioType: fiddleArpeggioType,
            });
            return (
              <NotesInKey
                size={isDesktop ? 'md' : 'sm'}
                orientation="vertical"
                heading={row.heading}
                notes={row.notes}
              />
            );
          })()}
          controls={
            <FiddleControls
              fiddleKey={fiddleKey}                 onFiddleKeyChange={setFiddleKey}
              fiddleTonality={fiddleTonality}       onFiddleTonalityChange={handleFiddleTonalityChange}
              fiddleStructure={fiddleStructure}     onFiddleStructureChange={handleFiddleStructureChange}
              fiddleMode={fiddleMode}               onFiddleModeChange={setFiddleMode}
              fiddleShape={fiddleShape}             onFiddleShapeChange={setFiddleShape}
              fiddlePosition={fiddlePosition}       onFiddlePositionChange={setFiddlePosition}
              fiddleStringFocus={fiddleStringFocus} onFiddleStringToggle={handleFiddleStringToggle}
              fiddleGuides={fiddleGuides}           onFiddleGuideToggle={handleFiddleGuideToggle}
              fiddleArpeggioType={fiddleArpeggioType} onFiddleArpeggioTypeChange={setFiddleArpeggioType}
            />
          }
          quickButtons={
            <FiddleQuickButtons
              fiddleStructure={fiddleStructure}
              fiddleFilter={fiddleFilter}   onFiddleFilterChange={setFiddleFilter}
              fiddleLabel={fiddleLabel}     onFiddleLabelChange={setFiddleLabel}
              onFiddleReset={handleFiddleReset}
            />
          }
        />
      )}

    <Analytics />
    </div>
  );
}

export default App;
